/**
 * Communications page - Display collective communications in member space
 */

import React, { useState, useEffect } from 'react'
import { Box, Typography, Card, CardContent, Grid, Alert } from '@mui/material'
import { useGetCommunications } from '../../hooks/api/communications'
import { useGetSession } from '../../hooks/api/users'
import { Communication } from '../../types/communications'
import Loader from '../../components/common/loader'
import useSelectedItemSideBar from '../../hooks/useSelectedItemSideBar'
import Title from '../../components/common/title'

const CommunicationsPage: React.FC = () => {
  useSelectedItemSideBar('communications')
  const [selectedCommunication, setSelectedCommunication] = useState<Communication | null>(null)

  // Get current user's contact ID
  const { data: session } = useGetSession()
  const contactId = session?.crm_id

  // Fetch communications for the current contact
  const { data: communications, isFetching } = useGetCommunications(contactId)


  // Ensure communications is an array
  const communicationsList = Array.isArray(communications) 
    ? communications 
    : (communications as any)?.data 
      ? Array.isArray((communications as any).data) 
        ? (communications as any).data 
        : []
      : []

  // Mark all communications as read when the page loads
  useEffect(() => {
    if (communicationsList.length > 0) {
      try {
        const readCommunications = JSON.parse(localStorage.getItem('readCommunications') || '[]')
        const allCommunicationIds = communicationsList.map((comm: Communication) => comm.id)
        const updatedReadCommunications = Array.from(new Set([...readCommunications, ...allCommunicationIds]))
        localStorage.setItem('readCommunications', JSON.stringify(updatedReadCommunications))
      } catch (error) {
        console.error('Error updating read communications:', error)
      }
    }
  }, [communicationsList])

  const formatDate = (dateString: string) => {
    try {
      const date = new Date(dateString)
      return date.toLocaleDateString('fr-FR', { 
        day: 'numeric', 
        month: 'long', 
        year: 'numeric' 
      })
    } catch {
      return dateString
    }
  }

  if (isFetching) {
    return <Loader />
  }

  return (
    <Box>
      <Title>Communications</Title>
      <Typography variant="body1" color="textSecondary" paragraph>
        Consultez les communications envoyées par l'ACQ
      </Typography>

      {communicationsList.length === 0 ? (
        <Alert severity="info">Aucune communication disponible pour le moment.</Alert>
      ) : (
        <Grid container spacing={3}>
          {communicationsList.map((communication: Communication) => (
            <Grid item xs={12} key={communication.id}>
              <Card
                sx={{
                  cursor: selectedCommunication?.id === communication.id ? 'default' : 'pointer',
                  '&:hover': {
                    boxShadow: selectedCommunication?.id === communication.id ? undefined : 6,
                  },
                  border: selectedCommunication?.id === communication.id ? '2px solid' : undefined,
                  borderColor: selectedCommunication?.id === communication.id ? 'primary.main' : undefined,
                }}
                onClick={() => {
                  if (selectedCommunication?.id === communication.id) {
                    setSelectedCommunication(null)
                  } else {
                    setSelectedCommunication(communication)
                  }
                }}
              >
                <CardContent>
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                    <Typography variant="h6">{communication.name}</Typography>
                    <Typography variant="caption" color="textSecondary">
                      {formatDate(communication.send_date || communication.date_entered)}
                    </Typography>
                  </Box>

                  {selectedCommunication?.id === communication.id ? (
                    <Box>
                      {communication.content_html ? (
                        <Box
                          dangerouslySetInnerHTML={{ __html: communication.content_html }}
                          sx={{
                            '& img': { maxWidth: '100%', height: 'auto' },
                            '& a': { color: 'primary.main' },
                          }}
                        />
                      ) : (
                        <Typography variant="body1" whiteSpace="pre-wrap">
                          {communication.content_text}
                        </Typography>
                      )}
                    </Box>
                  ) : (
                    <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                      {communication.content_text?.substring(0, 200)}
                      {communication.content_text?.length > 200 ? '...' : ''}
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}
    </Box>
  )
}

export default CommunicationsPage
